// App.jsx

import { useAuth } from "react-oidc-context";
import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useCurrentUser } from "./hooks/useCurrentUser.jsx";
import { isUserInGroup } from "./utils/userUtils.jsx";

import Sidebar from "./components/layout/Sidebar.jsx"; // likely not route-based, leave as is
import SignInPage from "./auth/SignInPage.jsx";
import "./App.css";

import { MdMenu } from "react-icons/md";
import { useMediaQuery } from "@mui/system";
import { Box, IconButton } from "@mui/material";
import NeuLoader from "./components/display/NeuLoader.jsx";

// Lazy-loaded route components
const Dashboard = lazy(() => import("./Dashboard/Dashboard.jsx"));
const UsagePage = lazy(() => import("./Usage/UsagePage.jsx"));
const FinancePage = lazy(() => import("./Finance/FinancePage.jsx"));
const Installations = lazy(() => import("./Installations/Installations"));
const InstallationDetails = lazy(
  () => import("./InstallationDetails/InstallationDetails"),
);
const Users = lazy(() => import("./Users/Users.jsx"));
const UserDetails = lazy(() => import("./Users/UserDetails.jsx"));
const Organizations = lazy(() => import("./organizations/Organizations.jsx"));
const OrganizationDetails = lazy(
  () => import("./organizations/OrganizationDetails.jsx"),
);
const GeneralSettingsForm = lazy(
  () => import("./Installations/Settings/InstallationSettings"),
);

const ServiceCasesPage = lazy( () => import("./operations/ServiceCasesPage.jsx"),
);

const OperationsMaintenancePage = lazy(
  () => import("./demo/pages/OperationsMaintenancePage.jsx"),
);

function App() {
  const { auth, isAuthenticated, isLoading } = useCurrentUser();
  const isMobile = useMediaQuery("(max-width:900px)");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const isDemo = isUserInGroup(auth.user, "LexEnergy-Demo");
  useEffect(() => {
    // Get the full URL and extract the code parameter
    const params = new URLSearchParams(window.location.search);
    const authCode = params.get("code");
    if (auth.isLoading) {
      return;
    }
    if (authCode) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [auth]);

  if (auth.isLoading) {
    return <NeuLoader fullScreen />;
  }

  if(!isAuthenticated) {
    return <SignInPage />
  }

  if (auth.error) {
    console.log("Got authentication error: " + auth.error?.message);
  }

  return (
    <Router>
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          boxSizing: "border-box",
          overflow: "hidden",
          bgcolor: "background.default",
        }}
      >
        {isMobile && !sidebarOpen && (
          <IconButton
            onClick={() => setSidebarOpen(true)}
            sx={(theme) => ({
              position: "fixed",
              top: 12,
              left: 12,
              zIndex: 1300,
              color: theme.palette.text.primary,
              ...theme.applyStyles("dark", {
                color: theme.palette.primary.main,
              }),
            })}
          >
            <MdMenu />
          </IconButton>
        )}

        <Sidebar
          onItemClick={() => {}}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          isMobile={isMobile}
          collapsed={sidebarCollapsed}
          onToggleCollapsed={() =>
            setSidebarCollapsed((prevCollapsed) => !prevCollapsed)
          }
        />

        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            overflowY: "auto",
            px: { xs: 0, md: 2.5 },
            py: { xs: 0, md: 0 },
          }}
        >
          <Suspense fallback={<NeuLoader fullScreen />}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/usage" element={<UsagePage />} />
              <Route path="/finance" element={<FinancePage />} />
              <Route
                path="/installations"
                element={
                  isDemo ? <OperationsMaintenancePage /> : <Installations />
                }
              />
              <Route
                path="/installations/:id"
                element={<InstallationDetails />}
              />
              <Route path="/users" element={<Users />} />
              <Route path="/users/:sub" element={<UserDetails />} />
              <Route
                path="/installations/:id/settings"
                element={<GeneralSettingsForm />}
              />
              <Route path="/organizations" element={<Organizations />} />
              <Route
                path="/organizations/:org"
                element={<OrganizationDetails />}
              />
              <Route path="/service-cases" element={<ServiceCasesPage />} />
            </Routes>
          </Suspense>
        </Box>
      </Box>
    </Router>
  );
}

export default App;
